import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { CalendarDays, MapPin, Users, Ticket as TicketIcon, BadgeCheck, Pencil } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Pill, Loader } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const FALLBACK =
  "https://images.unsplash.com/photo-1573152958734-1922c188fba3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxlbmVyZ2V0aWMlMjBjcm93ZCUyMGV2ZW50JTIwZGFya3xlbnwwfHx8fDE3ODYyMDQ5MTR8MA&ixlib=rb-4.1.0&q=85";

const loadRazorpay = () =>
  new Promise<boolean>((resolve) => {
    if ((window as any).Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const EventDetail = () => {
  const { id } = useParams();
  const { user, login } = useAuth();
  const [ev, setEv] = useState<any>(null);
  const [ticket, setTicket] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/events/${id}`).then((r) => setEv(r.data)).catch(() => setEv(false));
    if (user) api.get("/tickets/mine").then((r) => setTicket(r.data.find((t: any) => t.event_id === id) || null));
  };

  useEffect(load, [id, user]);

  const register = async () => {
    if (!user) return login();
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${id}/register`);
      setTicket(data);
      toast.success("You're in. Ticket sent to your email.");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const buy = async (tier: any) => {
    if (!user) return login();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { event_id: id, tier_id: tier.tier_id, quantity: 1 });
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay checkout");
      const rzp = new (window as any).Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "People's Circle",
        description: `${ev.title} — ${tier.name}`,
        theme: { color: "#FF003C" },
        prefill: { name: user.name, email: user.email, contact: user.phone || "" },
        handler: async (res: any) => {
          try {
            const v = await api.post("/orders/verify", res);
            setTicket(v.data.tickets[0]);
            toast.success("Payment confirmed. Ticket issued.");
            load();
          } catch (e) {
            toast.error(errMsg(e));
          } finally {
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  if (ev === null) return <Loader />;
  if (ev === false)
    return <div className="mx-auto max-w-[1400px] px-5 py-32 text-white/50" data-testid="event-not-found">Event not found.</div>;

  return (
    <div data-testid="event-detail-page">
      <div className="relative h-[46vh] min-h-[340px] overflow-hidden border-b border-white/10">
        <img src={ev.cover_image || FALLBACK} alt={ev.title} className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-base via-ink-base/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="mx-auto max-w-[1400px] px-5 lg:px-10 pb-10">
            <div className="flex gap-2">
              <Pill tone="neon">{ev.is_paid ? "Ticketed" : "Free entry"}</Pill>
              <Pill>{ev.category}</Pill>
            </div>
            <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
              <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-wide uppercase leading-[0.9] max-w-4xl" data-testid="event-title">
                {ev.title}
              </h1>
              {user && (user.user_id === ev.organizer_id || user.role === "admin") && (
                <Link to={`/events/${id}/edit`} data-testid="event-edit-link"
                  className="flex items-center gap-2 border border-white/20 px-5 h-11 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
                  <Pencil size={14} /> Edit
                </Link>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-6 text-[12px] uppercase tracking-[0.18em] text-white/55">
              <span className="flex items-center gap-2"><CalendarDays size={14} className="text-neon" /> {new Date(ev.starts_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
              <span className="flex items-center gap-2"><MapPin size={14} className="text-neon" /> {ev.venue}, {ev.city}</span>
              <span className="flex items-center gap-2"><Users size={14} className="text-neon" /> {ev.attendee_count} attending</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className="text-lg text-white/70 leading-relaxed">{ev.summary}</p>
          <div className="mt-8 whitespace-pre-line text-sm text-white/55 leading-relaxed" data-testid="event-description">
            {ev.description}
          </div>
          <div className="mt-12 border-t border-white/10 pt-6 flex items-center gap-3 text-sm text-white/50">
            <BadgeCheck size={16} className="text-pcblue" /> Hosted by <span className="text-white font-semibold">{ev.organizer_name}</span>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="border border-white/10 bg-ink-surface p-7 sticky top-24">
            {ticket ? (
              <div data-testid="event-ticket-block">
                <h3 className="font-bebas text-3xl tracking-wide uppercase">Your ticket</h3>
                <p className="mt-2 text-sm text-white/50">{ticket.tier_name} · {ticket.amount > 0 ? `₹${ticket.amount}` : "Free"}</p>
                <div className="mt-6 bg-white p-4 w-fit">
                  <QRCodeSVG value={ticket.qr_payload} size={148} />
                </div>
                <p className="mt-4 break-all text-[11px] uppercase tracking-widest text-white/35">{ticket.qr_payload}</p>
                {ticket.checked_in && <p className="mt-3 text-sm text-pcyellow">Checked in ✓</p>}
              </div>
            ) : ev.is_paid ? (
              <div data-testid="ticket-tiers">
                <h3 className="font-bebas text-3xl tracking-wide uppercase">Ticket tiers</h3>
                <div className="mt-6 grid gap-4">
                  {ev.tiers.map((t: any, i: number) => {
                    const soldOut = t.sold >= t.quantity;
                    return (
                      <div key={t.tier_id} className="border border-white/10 p-5 hover:border-neon/40 transition-colors" data-testid={`tier-${i}`}>
                        <div className="flex items-baseline justify-between">
                          <span className="font-outfit font-semibold">{t.name}</span>
                          <span className="font-bebas text-2xl tracking-wide text-neon">₹{t.price}</span>
                        </div>
                        {t.perks && <p className="mt-2 text-xs text-white/45">{t.perks}</p>}
                        <p className="mt-2 text-[11px] uppercase tracking-widest text-white/35">{t.quantity - t.sold} left</p>
                        <button
                          disabled={busy || soldOut}
                          onClick={() => buy(t)}
                          data-testid={`buy-ticket-${i}`}
                          className="mt-4 w-full h-11 bg-neon text-[12px] font-bold uppercase tracking-[0.18em] disabled:opacity-40 hover:pc-glow transition-shadow duration-200 flex items-center justify-center gap-2"
                        >
                          <TicketIcon size={15} /> {soldOut ? "Sold out" : "Buy ticket"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div data-testid="free-register-block">
                <h3 className="font-bebas text-3xl tracking-wide uppercase">Free entry</h3>
                <p className="mt-2 text-sm text-white/50">Reserve your spot and get a digital QR pass.</p>
                <button
                  onClick={register}
                  disabled={busy}
                  data-testid="register-free-btn"
                  className="mt-6 w-full h-12 bg-neon text-[12px] font-bold uppercase tracking-[0.18em] disabled:opacity-40 hover:pc-glow transition-shadow duration-200"
                >
                  {user ? "Get my pass" : "Sign in to register"}
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default EventDetail;
